import React, { useEffect, useState } from 'react';
import { adminApi, api, API_BASE } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Activity, Download, Loader2, Search, Users, Eye, Clock, TrendingUp, Filter, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const PAGE_LABELS = {
  '/': 'Home',
  '/loan': 'Personal Loans',
  '/protect-credit': 'Protect Credit',
  '/build-credit': 'Financials',
  '/how-it-works': 'How It Works',
  '/faq': 'FAQ',
  '/privacy-policy': 'Privacy Policy',
};

const pageLabel = (p) => PAGE_LABELS[p] || p;

export default function AdminAnalytics() {
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { toast } = useToast();

  const load = async (query = '') => {
    setLoading(true);
    try {
      const [s, ses] = await Promise.all([
        api.get('/analytics/summary').then((r) => r.data),
        api.get('/analytics/sessions', { params: query ? { q: query } : {} }).then((r) => r.data),
      ]);
      setSummary(s);
      setSessions(ses || []);
    } catch (e) {
      toast({ title: 'Failed to load analytics' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleSearch = (e) => { e.preventDefault(); load(q); };
  const clearSearch = () => { setQ(''); load(''); };

  const openDetail = async (sessionId) => {
    setDetailLoading(true);
    setDetail({ session_id: sessionId, pageviews: [] });
    try {
      const res = await api.get(`/analytics/sessions/${sessionId}`).then((r) => r.data);
      setDetail(res);
    } catch (e) { toast({ title: 'Failed to load session' }); }
    finally { setDetailLoading(false); }
  };

  const handleDownload = async () => {
    const token = localStorage.getItem('taa_token');
    const res = await fetch(`${API_BASE}/analytics/export/csv`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast({ title: 'Download failed' });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `pageviews_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const fmtDate = (v) => v ? new Date(v).toLocaleString() : '';
  const shortId = (s) => (s || '').length > 12 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s;
  const identity = (params) => {
    if (!params) return 'Anonymous';
    const name = params.name || `${params.firstName || ''} ${params.lastName || ''}`.trim();
    return name || params.email || params.phone || 'Anonymous';
  };

  const cards = [
    { label: 'Total Pageviews', value: summary?.total_pageviews ?? '–', icon: Eye, color: 'emerald' },
    { label: 'Unique Sessions', value: summary?.unique_sessions ?? '–', icon: Users, color: 'sky' },
    { label: 'Last 24 Hours', value: summary?.last_24h ?? '–', icon: Clock, color: 'amber' },
    { label: 'Last 7 Days', value: summary?.last_7d ?? '–', icon: TrendingUp, color: 'purple' },
  ];
  const colorMap = {
    emerald: 'from-emerald-500 to-teal-600',
    sky: 'from-sky-500 to-blue-600',
    amber: 'from-amber-500 to-orange-500',
    purple: 'from-violet-500 to-purple-600',
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-slate-900">Analytics</h1>
          <p className="text-slate-600 mt-1">User activity, sessions, and URL parameters across your site.</p>
        </div>
        <Button onClick={handleDownload} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl bg-white border border-slate-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{c.label}</div>
                <div className="mt-2 font-serif-display text-3xl font-bold text-slate-900">{c.value}</div>
              </div>
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorMap[c.color]} flex items-center justify-center text-white`}>
                <c.icon className="w-5 h-5" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Top pages */}
      {summary?.top_pages?.length > 0 && (
        <div className="rounded-xl bg-white border border-slate-200 p-5 mb-6">
          <div className="flex items-center gap-2 text-slate-900 font-semibold mb-3"><Activity className="w-4 h-4 text-emerald-600" /> Top Pages</div>
          <div className="space-y-2">
            {summary.top_pages.map((tp) => {
              const max = summary.top_pages[0].count || 1;
              const pct = Math.max(4, Math.round((tp.count / max) * 100));
              return (
                <div key={tp.page} className="grid grid-cols-[minmax(0,1fr)_60px] items-center gap-3 text-sm">
                  <div>
                    <div className="flex items-center justify-between text-slate-700">
                      <span>{pageLabel(tp.page)} <span className="text-xs text-slate-400">({tp.page})</span></span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="text-right font-semibold text-slate-800">{tp.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-10" placeholder="Search by name, email, phone, ZIP, session…" />
          {q && (
            <button type="button" onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Button type="submit" variant="outline"><Filter className="w-4 h-4 mr-1.5" /> Filter</Button>
      </form>

      {/* Sessions table */}
      {loading ? (
        <div className="flex justify-center py-14 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : sessions.length === 0 ? (
        <div className="rounded-xl bg-white border border-slate-200 p-10 text-center text-slate-500">
          <Users className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          No sessions match your filter.
        </div>
      ) : (
        <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase tracking-wider text-xs">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Session</th>
                  <th className="text-left px-4 py-3">Pages</th>
                  <th className="text-left px-4 py-3">Params</th>
                  <th className="text-left px-4 py-3">Last Seen</th>
                  <th className="text-right px-4 py-3">Views</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.session_id} onClick={() => openDetail(s.session_id)} className="border-b border-slate-100 hover:bg-emerald-50/40 cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900 truncate max-w-[180px]">{identity(s.params)}</div>
                      {s.params?.email && <div className="text-xs text-slate-500 truncate max-w-[180px]">{s.params.email}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-[11px] text-slate-500">{shortId(s.session_id)}</code>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(s.pages || []).slice(0, 4).map((p) => (
                          <span key={p} className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full">{pageLabel(p)}</span>
                        ))}
                        {(s.pages || []).length > 4 && <span className="text-[10px] text-slate-500">+{s.pages.length - 4}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-[220px]">
                      <div className="text-xs text-slate-600 truncate">
                        {['phone','zip','state','loanAmount'].filter((k) => s.params?.[k]).map((k) => (
                          <span key={k} className="mr-2"><span className="text-slate-400">{k}:</span> {s.params[k]}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(s.last_seen)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-800">{s.pageview_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Session Journey</DialogTitle></DialogHeader>
          {detail && (
            <div>
              <div className="text-xs text-slate-500 mb-3">
                <div><span className="font-semibold text-slate-700">Session:</span> <code>{detail.session_id}</code></div>
              </div>
              {detailLoading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : (
                <ol className="space-y-3">
                  {(detail.pageviews || []).map((pv, i) => (
                    <li key={pv.id || i} className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs flex items-center justify-center shrink-0">{i + 1}</div>
                      <div className="flex-1 rounded-lg border border-slate-200 p-3 bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-slate-900">{pageLabel(pv.page)} <span className="text-xs text-slate-400 font-normal">{pv.page}</span></div>
                          <div className="text-xs text-slate-500">{fmtDate(pv.created_at)}</div>
                        </div>
                        {pv.params && Object.keys(pv.params).length > 0 && (
                          <div className="mt-2 text-xs text-slate-600 flex flex-wrap gap-x-3 gap-y-1">
                            {Object.entries(pv.params).filter(([k]) => k !== '__search').map(([k, v]) => (
                              <span key={k}><span className="text-slate-400">{k}:</span> <span className="font-medium text-slate-800">{String(v)}</span></span>
                            ))}
                          </div>
                        )}
                        {pv.referrer && <div className="mt-1 text-[11px] text-slate-400 truncate">Referrer: {pv.referrer}</div>}
                      </div>
                    </li>
                  ))}
                  {(!detail.pageviews || detail.pageviews.length === 0) && !detailLoading && (
                    <div className="text-sm text-slate-500 py-4">No pageviews recorded.</div>
                  )}
                </ol>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
